import React from "react";
import { IconFileText } from "@tabler/icons-react";
import type { DcBarriersEvidence, DcBarriersRequirement } from "./types";

type Props = {
  requirement: DcBarriersRequirement;
  serialNumber: number;
  onEvidenceClick?: (evidence: DcBarriersEvidence) => void;
};

const DcBarriersCriteriaCard: React.FC<Props> = ({
  requirement,
  serialNumber,
  onEvidenceClick,
}) => {
  const isMet = requirement.Status === "Met";

  return (
    <div
      className={`grid grid-cols-[minmax(0,1fr)_auto_auto] items-start gap-x-3 rounded-lg border px-3 py-2 shadow-sm ${
        isMet
          ? "border-emerald-200 bg-emerald-50"
          : "border-red-200 bg-red-50"
      }`}
    >
      <div className="min-w-0">
        <div className="flex items-start">
          <span className="mr-2 inline-block min-w-[1.25rem] font-semibold tabular-nums text-slate-600">
            {serialNumber}.
          </span>
          <p className="text-sm font-bold leading-snug text-gray-900">
            {requirement.SignalName}
          </p>
        </div>

        {(requirement.Evidence ?? []).length > 0 && (
          <div className="mt-1.5 space-y-1.5">
            {(requirement.Evidence ?? []).map((evidence, index) => {
              const quote = evidence.highlight || evidence.text;
              return (
                <div
                  key={`${evidence.file}-${evidence.page}-${index}`}
                  className="flex items-start gap-1.5 pl-6"
                >
                  <IconFileText
                    size={14}
                    className="mt-0.5 shrink-0 text-indigo-400"
                    aria-hidden
                  />
                  <p className="min-w-0 text-xs italic leading-relaxed text-gray-800">
                    {quote}
                    <button
                      type="button"
                      onClick={() => onEvidenceClick?.(evidence)}
                      className="ml-1 inline font-semibold not-italic text-blue-600 hover:underline"
                    >
                      | {evidence.file} - p.{evidence.page}
                    </button>
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <span className="mt-0.5 rounded border border-gray-300 bg-white/70 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-600">
        {requirement.RequirementLevel}
      </span>

      <span
        className={`mt-0.5 inline-flex h-6 min-w-[6.25rem] items-center justify-center rounded-md px-2 text-[10px] font-semibold uppercase tracking-wide text-white ${
          isMet ? "bg-emerald-600" : "bg-red-600"
        }`}
      >
        {isMet ? "MET" : "NOT MET"}
      </span>
    </div>
  );
};

export default DcBarriersCriteriaCard;
